import i18n, { reloadResources } from 'i18next'
import { createRestApiClient, load } from 'scrivito'

import { getInstanceIdFromUrl } from '@/utils/getInstanceIdFromUrl'
import { PISA_BASE_URL } from '@/utils/getPisaBaseUrl'

export async function pisaUrl(): Promise<string> {
  const url = `${PISA_BASE_URL}/${getInstanceIdFromUrl()}`

  return url
}

export async function pisaClient(subPath: string) {
  const { url, headers } = await pisaConfig(subPath)

  return createRestApiClient(url, {
    headers,
    authViaInstance: getInstanceIdFromUrl(),
  })
}

export async function pisaConfig(subPath: string) {
  await load(() => reloadResources())
  return {
    url: `${await pisaUrl()}/${subPath}`,
    headers: {
      'Accept-Language': i18n?.resolvedLanguage || 'en',
    },
  }
}

export interface PisaRestApiConfig {
  url: string
  authViaInstance: string | undefined
  headers: { 'Accept-Language': string }
}

/**
 * Creates a REST API configuration for Pisa data classes.
 * This is an async function that returns a promise, which can be passed
 * directly to provideDataClass's restApi option.
 *
 * @param endpoint - The API endpoint path (e.g., 'lists', 'companies')
 * @returns Promise resolving to REST API configuration object
 */
export const createPisaRestApiConfig = async (
  endpoint: string,
): Promise<PisaRestApiConfig> => {
  await load(() => reloadResources())

  const baseUrl = `${PISA_BASE_URL}/${getInstanceIdFromUrl()}`

  return {
    url: `${baseUrl}/${endpoint}`,
    authViaInstance: getInstanceIdFromUrl(),
    headers: { 'Accept-Language': i18n?.resolvedLanguage || 'en' },
  }
}

/**
 * Creates a REST API configuration for Pisa data items (without i18n headers).
 * Used for endpoints like WhoAmI that don't require language headers.
 *
 * @param endpoint - The API endpoint path
 * @returns Promise resolving to REST API configuration object
 */
export const createPisaRestApiConfigWithoutHeaders = async (
  endpoint: string,
): Promise<Omit<PisaRestApiConfig, 'headers'>> => {
  const baseUrl = `${PISA_BASE_URL}/${getInstanceIdFromUrl()}`

  return {
    url: `${baseUrl}/${endpoint}`,
    authViaInstance: getInstanceIdFromUrl(),
  }
}
